import React, {useEffect} from 'react';
import {Autocomplete, Box, TextField} from '@mui/material';
import {useTheme} from "../../../contexts/theme";

interface StyledAutoCompleteProps {
    name: string;
    label: string;
    options: any[]; // Assuming options are an array of strings
    value?: any | any[]; // For non-react-hook-form usage, use this to control the value
    onChange?: (value: any, newValue: any) => void; // Callback for handling change in non-react-hook-form
    onInputChange?: (value: any, newValue: any) => void;
    freeSolo?: boolean;
    variant?: 'standard' | 'outlined';
    limitTags?: number;
    labelField?: string;
    valueField?: string;
    multiple?: boolean;
    disabled?: boolean;
    iconField?: string;
    required?: boolean;
    error?: boolean;
    helperText?: string;
    onBlur?: () => void;
    onKeyDown?: (e: React.KeyboardEvent<any>) => void;
}

const StyledAutoComplete: React.FC<StyledAutoCompleteProps> = ({
                                                                   name,
                                                                   label,
                                                                   options,
                                                                   value,
                                                                   onChange,
                                                                    variant = 'outlined',
    limitTags = 1,
    freeSolo = false,
    valueField,
    labelField,
    multiple = true,
    onKeyDown = () => {},
                                                                   iconField,
                                                                   disabled = false,
                                                                    required = false,
    helperText,
    error,
    onInputChange,
                                                                   onBlur = () => {},
                                                               }) => {

    const theme = useTheme();

    /*useEffect(() => {
        if(onChange && options && options.length === 1){
            onChange('', options[0]);
        }
    }, [onChange, options]);*/

    const customFilterOptions = (localOptions: any[], state: { inputValue: string }) => {
        return localOptions
            .filter((option: any) => labelField ? option[labelField].toLowerCase().includes(state.inputValue.toLowerCase()) :
                option.toLowerCase().includes(state.inputValue.toLowerCase())
            )
    };

    // For non-react-hook-form usage, directly use Autocomplete
    return (
        <Autocomplete
            onKeyDown={onKeyDown}
            freeSolo={freeSolo}
            multiple={multiple}
            limitTags={limitTags}
            options={options}
            filterSelectedOptions
            onChange={onChange}
            value={value}
            disabled={disabled}
            onInputChange={onInputChange}
            getOptionLabel={(option: any) => labelField && option.hasOwnProperty(labelField) ? option[labelField] : option}
            getOptionKey={(option: any) => valueField && option.hasOwnProperty(valueField) ? option[valueField] : option}
            isOptionEqualToValue={(option, value) => valueField ? option[valueField] === value[valueField] : option === value}
            renderOption={(props, option) => (
                <Box component={'li'} display={'flex'} alignItems={'center'} {...props}>
                    {iconField ? option[iconField] : <></>}
                    {labelField ? option[labelField] : option}
                </Box>
            )}
            sx={{
                "& .MuiAutocomplete-popupIndicator": {
                    color: theme.palette.lightGrey,
                },
                "& .MuiAutocomplete-clearIndicator": {
                    color: theme.palette.lightGrey,
                },
            }}

            renderInput={(params) => (
                <TextField
                    error={error}
                    helperText={helperText}
                    variant={variant}
                    required={required}
                    sx={{
                        '& .MuiInputBase-root.Mui-disabled': {
                            border: `${theme.border.width.thin} ${theme.border.type.solid} ${theme.palette.lightGrey}`,
                            color: theme.palette.lightGrey,
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                            border: `${theme.border.width.thin} ${theme.border.type.solid} ${theme.palette.white}`,
                        },
                        '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                                borderColor: theme.palette.white, // Default border color
                            },
                            '&:hover fieldset': {
                                borderColor: theme.palette.orange, // Border color on hover
                            },
                            '&.Mui-focused fieldset': {
                                borderColor: theme.palette.orange, // Border color when focused (orange)
                            },
                        },
                        '& .MuiInputBase-input': {
                            color: theme.palette.white,
                            '& fieldset': {
                                borderColor: theme.palette.white, // Default border color
                            },
                            '&:hover fieldset': {
                                borderColor: theme.palette.orange, // Border color on hover
                            },
                            '&.Mui-focused fieldset': {
                                borderColor: theme.palette.orange, // Border color when focused (orange)
                            },
                        },
                        '& .MuiInput-underline:after': {
                        borderBottomColor: theme.palette.orange, // Change focus underline color here
                        },

                        '& .MuiFormLabel-root': {
                            color: theme.palette.white,
                        },
                        // Custom style for the placeholder
                        '& .MuiInputBase-input::placeholder': {
                            color: theme.palette.white,
                        },
                        '& .MuiFormLabel-root.Mui-focused': {
                            color: theme.palette.orange, // Label color when focused (orange)
                        },
                        '& .MuiChip-root':{
                            color: theme.palette.blue,
                            backgroundColor: theme.palette.orange,
                        },
                    }}
                    {...params}
                    onBlur={onBlur}
                    label={label}
                    placeholder={label}
                    InputLabelProps={{
                        sx: {
                            color: theme.palette.white + " !important", // default label color
                            "&.Mui-focused": {
                                color: "blue", // when the input is focused
                            },
                            "&.Mui-disabled": {
                                color:  theme.palette.lightGrey + " !important", // if disabled
                            },
                        },
                    }}
                />
            )}
        />
    );
};

export default StyledAutoComplete;
